import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Heart, ArrowLeft, X } from "lucide-react";

export function ExitLevelWarning({ isOpen, onConfirm, onCancel, hp, maxHP }) {
  const hpPercentage = Math.round((hp / maxHP) * 100);
  
  return (
    <Dialog open={isOpen} onOpenChange={onCancel}>
      <DialogContent className="sm:max-w-md bg-gradient-to-br from-red-50 to-orange-50 border-2 border-red-300">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-2xl text-red-700">
            <AlertTriangle className="w-6 h-6 text-red-500" />
            Wait!
          </DialogTitle>
          <DialogDescription className="text-amber-800 text-base pt-2">
            If you quit now, you will lose 20 HP!
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {/* Visual HP bar */}
          <div className="p-4 bg-white/70 rounded-xl border border-red-200 space-y-3">
            <div className="flex justify-between items-center text-sm font-medium">
              <span className="flex items-center gap-1">
                <Heart className={`w-5 h-5 ${hpPercentage > 60 ? 'text-green-500' : hpPercentage > 30 ? 'text-yellow-500' : 'text-red-500'} fill-current`} />
                Current HP
              </span>
              <span className={`font-bold ${hpPercentage > 60 ? 'text-green-600' : hpPercentage > 30 ? 'text-yellow-600' : 'text-red-600'}`}>
                {hp} / {maxHP}
              </span>
            </div>
            
            {/* HP Bar */}
            <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all duration-500 ${
                  hpPercentage > 60 ? 'bg-gradient-to-r from-green-500 to-emerald-400' : 
                  hpPercentage > 30 ? 'bg-gradient-to-r from-yellow-500 to-amber-400' : 
                  'bg-gradient-to-r from-red-500 to-rose-400'
                }`}
                style={{ width: `${hpPercentage}%` }}
              />
            </div>
            
            {/* After quitting preview */}
            <div className="pt-2 border-t border-gray-200">
              <div className="flex justify-between items-center text-sm text-gray-600">
                <span>After quitting:</span>
                <span className={`font-bold ${Math.max(0, hp - 20) <= 0 ? 'text-red-600' : 'text-orange-600'}`}>
                  {Math.max(0, hp - 20)} / {maxHP} HP
                </span>
              </div>
              <div className="h-3 bg-gray-200 rounded-full overflow-hidden mt-1">
                <div 
                  className="h-full rounded-full bg-gradient-to-r from-red-400 to-orange-400 transition-all duration-500"
                  style={{ width: `${Math.round((Math.max(0, hp - 20) / maxHP) * 100)}%` }}
                />
              </div>
            </div>
          </div>

          <div className="text-center space-y-2">
            <p className="text-sm text-amber-700">
              You have <span className="font-bold text-red-600">{hp} HP</span> remaining.
            </p>
            <p className="text-xs text-amber-600">
              HP regenerates 20 every 10 minutes.
            </p>
          </div>

          {/* Warning box */}
          <div className="p-3 bg-red-100/70 rounded-lg border border-red-200">
            <p className="text-sm text-red-700 text-center font-medium">
              💔 Quitting will cost you 20 HP
            </p>
          </div>
        </div>

        <DialogFooter className="flex gap-2 sm:gap-2">
          <Button
            onClick={onCancel}
            className="flex-1 font-display bg-green-500 hover:bg-green-600 text-white"
            data-testid="continue-playing-btn"
          >
            <X className="w-4 h-4 mr-2" />
            Keep Playing
          </Button>
          <Button
            onClick={onConfirm}
            variant="outline"
            className="flex-1 font-display border-red-300 text-red-700 hover:bg-red-50"
            data-testid="confirm-exit-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Quit
            <span className="ml-1 text-xs">(-20 HP)</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
